<script setup>
import { onMounted, ref, computed, defineEmits } from 'vue';
import { $api } from '@/services';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import Checkbox from '@/components/elements/checkbox/Checkbox';
import CreateGoalModal from '@/views/createGoalByLib/CreateGoalModal';
import { useTutorial } from '@/features/tutorial/useTutorial';
import { isTutorialVirtualStudent } from '@/features/tutorial/virtualStudent';

const modalIsOpen = ref(false);
const goalPayloadData = ref(null);
const error = ref('');
const skills = ref([]);
const emit = defineEmits(['updated']);
const store = useStore();
const { isAddGoalTour, advanceSpotlight, activeSpotlight } = useTutorial();
const goalTaught = computed(() => store.getters.goalTaught || []);
const suggestionGoal = ref([]);
const isLoadingSuggestionGoal = ref(true);
const route = useRoute();
const alertShow = ref(true);
const goalsSelected = computed(() => {
	const selected = store.getters.goalsSelected;
	return (selected || []).map((i) => i.id);
});
const goalCount = ref(0);
const skillCount = ref(0);
const showSuggestionGoal = ref(true);
const fetchSuggestionGoal = async () => {
	try {
		const res = await $api.goal.fetchSuggestionGoal(route.params.id);
		if (res.data) {
			suggestionGoal.value = res.data.grouped_items;
			goalCount.value = res.data.item_count;
			skillCount.value = res.data.skill_count;
		}
	} catch (e) {
		await store.dispatch('setAlertMessage', {
			message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		isLoadingSuggestionGoal.value = false;
	}
};

const addMultiGoal = async () => {
	if (goalsSelected.value.length == 0 && !isAddGoalTour.value) {
		return;
	}
	try {
		if (!isTutorialVirtualStudent(route.params.id)) {
			await $api.goal.addMultiGoal(route.params.id, {
				library_item_ids: goalsSelected.value,
			});
		}
		await store.dispatch('setAlertMessage', {
			message: 'Đã thêm mục tiêu thành công',
		});
		for (let i of goalsSelected.value) {
			await store.dispatch('taughtGoal', i);
		}
		emit('updated');
		if (
			isAddGoalTour.value &&
			activeSpotlight.value?.targetId === 'bank-add-selected'
		) {
			advanceSpotlight();
		}
	} catch (e) {
		await store.dispatch('setAlertMessage', {
			message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};

const fetchSkill = async () => {
	try {
		const res = await $api.goal.allSkills();
		if (res.data) {
			skills.value = res.data.data;
		}
	} catch (e) {
		console.log(e);
	}
};
const toggleGoalSelected = (goal) => {
	store.dispatch('selected', { goals: [goal], isAll: false });
};

const isSkillAllSelected = (skill) => {
	return (
		skill.items?.length > 0 &&
		skill.items.every((goal) => goalsSelected.value.includes(goal.id))
	);
};

const toggleSkillSelected = (skill) => {
	store.dispatch('selected', {
		goals: skill.items || [],
		isAll: !isSkillAllSelected(skill),
	});
};

const doGoal = (goal) => {
	modalIsOpen.value = true;
	goalPayloadData.value = {
		...goal,
		library_item_id: goal?.goal_library?.id,
		id: goal?.id,
		name: goal?.name || goal?.goal_name,
	};
};
const updated = () => {
	goalsSelected.value.push(goalPayloadData.value?.id);
	emit('updated');
	modalIsOpen.value = false;
	goalPayloadData.value = null;
};

onMounted(() => {
	fetchSuggestionGoal();
	fetchSkill();
});
</script>
<template>
	<div :class="modalIsOpen ? 'side-over--active' : ''">
		<div
			class="fixed h-screen w-screen top-0 left-0 bg-black opacity-20 z-30"
			v-if="modalIsOpen"
			@click="modalIsOpen = false"
		></div>
		<CreateGoalModal
			:show="modalIsOpen"
			v-model:skills="skills"
			v-model:data="goalPayloadData"
			@close="modalIsOpen = false"
			@updated="updated"
		/>
	</div>
	<div class="flex items-center justify-end mb-6">
		<button
			data-tour-id="bank-add-selected"
			class="delete-button flex items-center whitespace-nowrap"
			:class="{
				'disable-button':
					goalsSelected.length == 0 && !isAddGoalTour,
			}"
			@click="addMultiGoal"
		>
			Thêm mục tiêu đã chọn
		</button>
	</div>
	<div class="mb-10" v-if="alertShow">
		<div class="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded-md">
			<div class="relative">
				<div>
					<img
						src="@/assets/images/logo/logo.svg"
						alt="luca-logo"
						height="24"
						width="35"
					/>
				</div>
				<div class="mt-3 text-neutral-600 text-sm">
					Bạn hãy thêm mục tiêu từ Thư viện nội bộ vào Ngân hàng mục
					tiêu.
				</div>
				<div
					class="top-1 right-2 absolute cursor-pointer"
					@click="alertShow = false"
				>
					<svg
						width="12"
						height="12"
						viewBox="0 0 12 12"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L6 4.58579L10.2929 0.292893C10.6834 -0.0976311 11.3166 -0.0976311 11.7071 0.292893C12.0976 0.683417 12.0976 1.31658 11.7071 1.70711L7.41421 6L11.7071 10.2929C12.0976 10.6834 12.0976 11.3166 11.7071 11.7071C11.3166 12.0976 10.6834 12.0976 10.2929 11.7071L6 7.41421L1.70711 11.7071C1.31658 12.0976 0.683417 12.0976 0.292893 11.7071C-0.0976311 11.3166 -0.0976311 10.6834 0.292893 10.2929L4.58579 6L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z"
							fill="#E5A42B"
						/>
					</svg>
				</div>
			</div>
		</div>
	</div>
	<div v-if="isLoadingSuggestionGoal"></div>
	<div v-else>
		<div v-if="suggestionGoal.length">
			<div class="relative mb-4">
				<div
					aria-hidden="true"
					class="absolute inset-0 flex items-center"
				>
					<div class="w-full border-t border-neutral-300" />
				</div>
				<div class="relative flex items-center justify-between">
					<div
						class="flex items-center gap-2 pr-3 bg-body text-lg font-semibold text-neutral-600"
					>
						<img
							v-if="showSuggestionGoal"
							@click="showSuggestionGoal = !showSuggestionGoal"
							src="@/assets/images/icons/arrow-down.png"
							alt=""
							class="cursor-pointer"
						/>
						<img
							v-else
							@click="showSuggestionGoal = !showSuggestionGoal"
							src="@/assets/images/icons/arrow-up.png"
							alt=""
							class="cursor-pointer"
						/>
						<span>Gợi ý mục tiêu tiếp theo</span>
					</div>
					<div class="flex items-center gap-2 bg-body pl-3">
						<div class="text-neutral-400 text-xs font-semibold">
							MỤC TIÊU: {{ goalCount }}
						</div>
						<div class="text-neutral-400 text-xs font-semibold">
							LĨNH VỰC: {{ skillCount }}
						</div>
					</div>
				</div>
			</div>
			<div
				v-show="showSuggestionGoal"
				v-for="(skill, index) in suggestionGoal"
				:key="skill.id"
				:class="{ 'mb-6': index !== suggestionGoal.length - 1 }"
			>
				<div class="relative ml-10 mb-6">
					<div
						aria-hidden="true"
						class="absolute inset-0 flex items-center"
					>
						<div class="w-full border-t border-neutral-300" />
					</div>
					<div class="relative flex items-center justify-between">
						<div class="flex items-center gap-2 bg-body">
							<img
								v-if="skill.hide"
								@click="
									skill.hide = !skill.hide
								"
								src="@/assets/images/icons/arrow-up.png"
								
								alt=""
								class="cursor-pointer"
							/>
							<img
								v-else
								@click="
									skill.hide = !skill.hide
								"
								src="@/assets/images/icons/arrow-down.png"
								alt=""
								class="cursor-pointer"
							/>
							<span
								class="pr-3 bg-body text-lg font-semibold text-neutral-600"
							>
								{{ index + 1 }}. {{ skill.skill_name }}
							</span>
						</div>
						<div
							class="text-neutral-400 text-xs font-semibold bg-body pl-3"
						>
							MỤC TIÊU: {{ skill.item_count }}
						</div>
					</div>
				</div>
				<div class="shadow-sm sm:rounded-2xl" v-if="!skill.hide">
					<div class="__record__table scroll-bar">
						<div class="sm:rounded-t-2xl w-fit min-w-full">
							<table
								class="min-w-full divide-y text-neutral-200 rounded-2xl"
							>
								<thead class="bg-neutral-100 h-16 rounded-2xl">
									<tr>
										<th
											scope="col"
											class="pl-3 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										>
											<Checkbox
												:value="
													isSkillAllSelected(skill)
												"
												@click="
													toggleSkillSelected(skill)
												"
											/>
										</th>
										<th
											scope="col"
											class="p-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										>
											STT
										</th>
										<th
											scope="col"
											class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										>
											mục tiêu
										</th>
										<th
											scope="col"
											class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										></th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="(goal, i) in skill.items"
										:key="goal.id"
										:class="
											i % 2 === 0
												? 'bg-white'
												: 'bg-neutral-50'
										"
										class="text-neutral-700 cursor-pointer hover:bg-orange-50"
									>
										<td
											class="py-3 pl-3 whitespace-nowrap text-neutral-700"
										>
											<Checkbox
												:value="
													goalsSelected.includes(
														goal.id
													)
												"
												@click="
													toggleGoalSelected(goal)
												"
											/>
										</td>
										<td
											class="p-3 whitespace-nowrap text-neutral-700 text-center"
										>
											{{ i + 1 }}
										</td>
										<td class="py-4 text-neutral-700">
											<div v-html="goal?.name"></div>
											<div
												class="goal-block flex gap-3 mt-2"
											>
												<img
													class="w-3 h-3 mt-1"
													src="@/assets/images/icons/goal-arrow.png"
													alt=""
												/>
												<div>
													<div
														class="text-neutral-700"
													>
														{{ goal.parent?.name }}
													</div>
													<div
														class="text-neutral-400 text-xs font-semibold"
													>
														{{
															goal.parent
																?.sub_item_count
														}}
														MỤC TIÊU
													</div>
												</div>
											</div>
											<div
												class="flex items-start gap-2 mt-1"
											>
												<div class="w-4 mt-1">
													<svg
														width="16"
														height="16"
														viewBox="0 0 16 16"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															fill-rule="evenodd"
															clip-rule="evenodd"
															d="M14.4115 5.52987C14.3596 5.06269 14.3596 4.28406 14.7749 4.02452C14.7749 4.02452 14.7749 4.02452 14.8268 4.02452C15.1383 3.9207 15.3978 3.76497 15.1902 3.55734L10.1031 2.00008L1.84963 3.14207C1.84963 3.14207 0.91527 3.29779 0.967178 4.75124C1.01909 5.52987 1.27863 5.94514 1.48627 6.15277L0.811452 6.36041C0.655726 6.56804 0.863361 6.72377 1.22672 6.82758C1.64199 7.08713 1.64199 7.86576 1.59008 8.33294C0.0847304 8.74821 0.603817 8.90393 0.603817 8.90393L0.967178 9.00775C0.707635 9.21538 0.5 9.63065 0.5 10.3574C0.603817 11.8627 1.38245 11.9146 1.38245 11.9146L6.78095 13.6795L14.9306 11.707C14.9306 11.707 15.5016 11.6032 13.9963 11.136C13.8924 10.6688 13.8924 9.8902 14.3077 9.63065C14.3596 9.63065 14.3596 9.63065 14.3596 9.63065C14.6711 9.52684 14.9306 9.37111 14.723 9.16348L14.4115 9.05966C14.6192 8.90393 14.9825 8.54057 15.0344 7.55431C15.0863 6.87949 14.8787 6.51613 14.723 6.25659L15.3978 6.10086C15.3978 6.10086 15.9688 5.94514 14.4115 5.52987ZM7.61149 5.52987L8.96112 5.21842L13.6329 4.28406L14.3077 4.12833C14.1001 4.5436 14.1001 5.1146 14.152 5.42605C14.152 5.52987 14.152 5.58178 14.152 5.68559L13.4253 5.84132L7.55958 7.29476L7.61149 5.52987ZM1.6939 6.9314L2.42062 7.08713L6.83286 8.02148L7.24813 8.07339L8.39012 8.33294L8.49394 10.0978L2.47253 8.59248L1.84963 8.48866C1.84963 8.43675 1.90153 8.33294 1.90153 8.28103C1.90153 7.91767 1.95344 7.34667 1.6939 6.9314ZM1.43436 4.69933C1.43436 4.28406 1.48627 4.02452 1.64199 3.86879C1.74581 3.76497 1.84963 3.76497 1.90153 3.76497C1.95344 3.76497 2.00535 3.76497 2.00535 3.76497L5.53514 4.90696L7.30004 5.47796L7.24813 7.29476L2.2649 5.89323L2.00535 5.84132C1.95344 5.84132 1.48627 5.78941 1.43436 4.69933ZM6.78095 12.9009L1.53817 11.4475C1.48627 11.4475 1.01909 11.3955 0.967178 10.3055C0.967178 9.8902 1.071 9.63065 1.17481 9.47493C1.27863 9.37111 1.38245 9.37111 1.43436 9.37111C1.48627 9.37111 1.53817 9.37111 1.53817 9.37111L6.88477 11.136L6.78095 12.9009ZM13.6848 11.0841C13.6848 11.136 13.6848 11.2398 13.6848 11.2917L7.0924 12.9009L7.14431 11.136L8.54585 10.8246L8.75348 10.8765L9.53211 10.6169L13.1657 9.8902L13.8405 9.73447C13.6329 10.1497 13.6329 10.7207 13.6848 11.0841ZM14.0482 8.64439H13.9963L13.5291 8.80011L8.75348 10.0978L8.70157 8.33294L10.2588 7.81385L14.0482 6.56804C14.0482 6.56804 14.2039 6.51613 14.3596 6.67186C14.5153 6.82758 14.5673 7.08713 14.5673 7.5024C14.5673 8.59248 14.0482 8.64439 14.0482 8.64439Z"
															fill="#94A3B8"
														/>
													</svg>
												</div>
												<div
													class="text-neutral-400 font-semibold text-sm"
												>
													{{
														goal?.goal_library?.name
													}}
												</div>
											</div>
										</td>
										<td class="w-12">
											<div
												v-if="
													goalTaught.includes(goal.id)
												"
												class="font-semibold text-green-500 text-xs"
											>
												Đã thêm
											</div>
											<div
												@click="doGoal(goal)"
												v-else
												class="px-4 cursor-pointer text-yellow-600 hover:text-yellow-700 font-semibold text-sm flex items-center md-down:items-right whitespace-nowrap"
											>
												Thêm MT
											</div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div
			v-else-if="!isAddGoalTour"
			class="w-full h-full flex items-center justify-center flex-col"
		>
			<img
				src="@/assets/images/illustrations/not_found.png"
				alt=""
				height="250"
				width="250"
			/>
			<div
				class="text-neutral-400 text-lg text-center"
				style="max-width: 900px"
			>
				Trẻ cần hoàn thành ít nhất 1 mục tiêu để Luca gợi ý các mục tiêu
				tiếp theo cho trẻ
			</div>
		</div>
	</div>
</template>
<style scoped>
.delete-button {
	padding: 12px 20px;
	gap: 6px;
	border-radius: 4px;
	border: 1px solid #d89210;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
	color: #d89210;
}
.disable-button {
	background: #e1e7ef !important;
	border: 1px solid #cbd5e1 !important;
	color: #cbd5e1;
}
</style>